"use client";

import { useFeatureFlagVariantKey } from "posthog-js/react";
import { ComponentPropsWithoutRef, FC } from "react";
import FAQ from "../../../components/FAQ";
import Footer from "../../../components/Footer";
import Header from "../../../components/Header";
import PricingCards from "../../../components/PricingCards";
import TrustedBy from "../../../components/TrustedBy";
import Typography from "../../../components/Typography";
import { publicPlansAtProPrice } from "../../../const/publicPricing";
import {
  pricing as enPricing,
  type PricingCopy,
} from "../../../dictionaries/en/pricing";
import { PRO_MONTHLY_PRICE } from "../../../helpers/proPriceExperiment";
import { usePricingLimitVariant } from "../../../hooks/usePricingLimitVariant";
import { useProPriceVariant } from "../../../hooks/useProPriceVariant";

type PricingProps = {
  // This page's text in the visitor's language.
  copy?: PricingCopy;
} & ComponentPropsWithoutRef<"main">;

const PricingContent: FC<PricingProps> = ({ copy = enPricing, ...rest }) => {
  const cardRequiredFreeTrialKey = useFeatureFlagVariantKey(
    "card-required-free-trial",
  );
  const isCardRequiredFreeTrial =
    cardRequiredFreeTrialKey === "all_plans" ||
    cardRequiredFreeTrialKey === "basic_only";
  const pricingLimitVariant = usePricingLimitVariant();

  // The HTML states the public prices. An arm applies here, client-side, so
  // the FAQ agrees with the cards.
  const plans = publicPlansAtProPrice(PRO_MONTHLY_PRICE[useProPriceVariant()]);

  const subtitle =
    pricingLimitVariant === "control" && isCardRequiredFreeTrial
      ? copy.tryForFree
      : copy.subtitle(pricingLimitVariant);

  return (
    <main {...rest} className={`${rest.className} `}>
      <Header />
      <div className={`max-w-5xl mx-auto px-3 text-center text-slate-950`}>
        <Typography variant="h2" component="h1" className="my-8">
          {copy.title}
        </Typography>
        <Typography variant="body" className="text-slate-600 my-8">
          {subtitle}
        </Typography>

        <PricingCards />

        <div className="flex items-center justify-center">
          <TrustedBy className="mt-16 mx-auto" />
        </div>

        <FAQ
          className="max-w-3xl mx-auto mt-24 mb-24"
          faq={copy.faq(plans)}
          jsonLd
        />
      </div>

      <Footer />
    </main>
  );
};

export default PricingContent;
